import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import type { BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import { fetchMessage } from './api';
import type { IntegrationMessageInfo, MessageCriteria, MessageStatus } from './api';
import { LazyPayload } from './parts';
import { ROWS_OPTIONS, useMonitorParams } from './useMonitorParams';
import { useDeliveries, useSourceCodes } from './useMonitor';
import styles from './MonitorPage.module.css';

/**
 * The delivery journal: what actually arrived, payload included.
 *
 * **Few rows here is healthy.** A poll whose content had not changed is not stored
 * at all — the lead line says so, because a quiet feed on this list otherwise reads
 * as a dead one. Liveness is the health light on the sources view, never a row
 * count here.
 */
export const MessagesView = () => {
  const { params, update } = useMonitorParams();
  const sourceCodes = useSourceCodes();
  const { t } = useTranslation();

  const criteria = useMemo<MessageCriteria>(
    () => ({ sourceCode: params.source, pageNum: params.page, rows: params.rows }),
    [params.source, params.page, params.rows],
  );

  const { rows, currPage, total, capped, isLastPage, isLoading, errorKey } =
    useDeliveries(criteria);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  /**
   * The dropdown's options, from the server's own source list.
   *
   * The current value is folded in even when the list does not contain it — the
   * codes arrive a moment after the first paint, and a native select whose value is
   * not among its options renders BLANK, so a filtered feed would briefly look
   * unfiltered. It also keeps a stale bookmarked code visible rather than silently
   * showing the reader a filter they cannot see.
   */
  const sourceOptions = useMemo(() => {
    const codes = params.source !== '' && !sourceCodes.includes(params.source)
      ? [params.source, ...sourceCodes]
      : sourceCodes;

    return codes.map((code) => ({ value: code, label: code }));
  }, [sourceCodes, params.source]);

  return (
    <div>
      <div className={styles.filters}>
        <label className={styles.filter}>
          <span className={styles.filterLabel}>{t('monitor.sources.columns.source')}</span>
          <Select
            options={sourceOptions}
            placeholder={t('monitor.events.allSources')}
            value={params.source}
            active={params.source !== ''}
            onChange={(event) => update({ source: event.target.value })}
            aria-label={t('monitor.sources.columns.source')}
          />
        </label>

        <Button size="sm" onClick={() => update({ source: '' })} disabled={params.source === ''}>
          {t('monitor.clearFilters')}
        </Button>
      </div>

      <p className={styles.note}>{t('monitor.messages.lead')}</p>

      {errorKey && (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      )}

      <div className={styles.tableWrap}>
        {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

        <table className={cx(styles.table, isLoading && styles.loading)}>
          <thead>
            <tr>
              <th>{t('monitor.events.columns.time')}</th>
              <th>{t('monitor.sources.columns.source')}</th>
              <th>{t('monitor.messages.columns.kind')}</th>
              <th>{t('monitor.messages.columns.status')}</th>
              <th>{t('monitor.messages.columns.rows')}</th>
              <th>{t('monitor.messages.columns.ref')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={6} className={styles.message}>
                  {t('monitor.messages.nothingFound')}
                </td>
              </tr>
            ) : (
              rows.map((delivery) => (
                <DeliveryRow
                  key={delivery.id}
                  delivery={delivery}
                  isExpanded={expandedId === delivery.id}
                  onToggle={() =>
                    setExpandedId((current) => (current === delivery.id ? null : delivery.id))
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={currPage}
        rows={params.rows}
        total={total}
        capped={capped}
        countLabel={t('monitor.messages.found')}
        isLastPage={isLastPage}
        rowsOptions={ROWS_OPTIONS}
        onPage={(page) => update({ page })}
        onRows={(rowCount) => update({ rows: rowCount })}
        isLoading={isLoading}
      />
    </div>
  );
};

const STATUS_TONE: Record<MessageStatus, BadgeTone> = {
  PROCESSED: 'success',
  // Legacy: no longer written, still on old rows.
  UNCHANGED: 'neutral',
  FAILED: 'danger',
};

const DeliveryRow = ({
  delivery,
  isExpanded,
  onToggle,
}: {
  delivery: IntegrationMessageInfo;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const { t, formatDateTime, formatNumber } = useTranslation();

  return (
    <>
      <tr className={cx(styles.row, isExpanded && styles.selected)} onClick={onToggle}>
        <td className={styles.timeCell}>{formatDateTime(delivery.created)}</td>
        <td className={styles.mono}>{delivery.sourceCode ?? <span className={styles.empty}>—</span>}</td>
        <td className={styles.mono}>{delivery.messageType ?? <span className={styles.empty}>—</span>}</td>

        <td>
          <Badge tone={STATUS_TONE[delivery.status]}>
            {t(`monitor.messages.statuses.${delivery.status}`)}
          </Badge>
        </td>

        {/* Received against processed is the delivery's whole story in two numbers:
            1668 arrived, 4 changed something. */}
        <td className={styles.numCell}>
          {delivery.rowsReceived === null ? (
            <span className={styles.empty}>—</span>
          ) : (
            `${delivery.rowsProcessed === null ? '—' : formatNumber(delivery.rowsProcessed)} / ${formatNumber(delivery.rowsReceived)}`
          )}
        </td>

        <td className={styles.detailsCell}>
          {delivery.externalRef ?? <span className={styles.empty}>—</span>}
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.expandRow}>
          <td colSpan={6}>
            <div className={styles.expanded}>
              {delivery.errorText && <pre className={styles.errorText}>{delivery.errorText}</pre>}

              {/*
                Gated on the server's flag. A successful airport snapshot keeps no
                file on purpose — the useful half is each flight's own record on its
                event, and the 1.5 MB file was not — so `false` here is ordinary and
                is said plainly rather than fetched and reported as an absence.
              */}
              {delivery.hasRawPayload ? (
                <LazyPayload
                  emptyKey="monitor.payloadEmpty"
                  load={() => fetchMessage(delivery.id).then((full) => full.rawPayload)}
                />
              ) : (
                <p className={styles.payloadNote}>{t('monitor.deliveryNoContent')}</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
